/**
 * Servicio para gestionar la integración con Hotjar
 * 
 * Este servicio proporciona una capa de abstracción para interactuar
 * con Hotjar, permitiendo identificar usuarios y registrar eventos personalizados.
 */

import { HOTJAR_ID, HOTJAR_EVENTS } from './hotjarConfig';

// Exportamos los eventos por conveniencia
export { HOTJAR_EVENTS };

// Verifica si Hotjar está disponible en el entorno actual
const isHotjarAvailable = () => {
  return typeof window !== 'undefined' && typeof window.hj === 'function';
};

/**
 * Identifica al usuario en Hotjar
 * @param {string} userId - ID único del usuario
 * @param {Object} attributes - Atributos adicionales del usuario (opcional)
 * @returns {boolean} - True si la identificación fue exitosa
 */
export const identifyHotjarUser = (userId, attributes = {}) => {
  if (!isHotjarAvailable() || !userId) {
    console.warn('[Hotjar] No disponible o userId no proporcionado');
    return false;
  }

  try {
    // Combinar ID con atributos
    const userAttributes = {
      id: userId,
      ...attributes
    };

    // Llamar a la API de identificación de Hotjar
    window.hj('identify', userId, userAttributes);
    
    console.log(`[Hotjar] Usuario identificado: ${userId}`, attributes);
    return true;
  } catch (error) {
    console.error('[Hotjar] Error al identificar usuario:', error);
    return false;
  }
};

/**
 * Registra un evento personalizado en Hotjar
 * @param {string} eventName - Nombre del evento a registrar
 * @returns {boolean} - True si el evento se registró correctamente
 */
export const trackHotjarEvent = (eventName) => {
  if (!isHotjarAvailable()) {
    console.warn('[Hotjar] No disponible para rastrear eventos');
    return false;
  }

  try {
    // Registrar evento en Hotjar
    window.hj('event', eventName);
    
    console.log(`[Hotjar] Evento registrado: ${eventName}`);
    return true;
  } catch (error) {
    console.error(`[Hotjar] Error al registrar evento ${eventName}:`, error);
    return false;
  }
};

/**
 * Establece variables de estado para la sesión actual de Hotjar
 * @param {Object} stateVars - Variables de estado a establecer
 * @returns {boolean} - True si las variables se establecieron correctamente
 */
export const setHotjarState = (stateVars = {}) => {
  if (!isHotjarAvailable() || !stateVars || Object.keys(stateVars).length === 0) {
    console.warn('[Hotjar] No disponible o variables de estado no proporcionadas');
    return false;
  }

  try {
    // Establecer variables de estado en Hotjar
    window.hj('stateChange', stateVars);
    
    console.log('[Hotjar] Variables de estado establecidas:', stateVars);
    return true;
  } catch (error) {
    console.error('[Hotjar] Error al establecer variables de estado:', error);
    return false;
  }
};

// Comentario para documentar los eventos de demo disponibles en HOTJAR_EVENTS
// Los eventos incluyen: DEMO_VIEWED, DEMO_DOG_SELECTED, DEMO_ANALYSIS_STARTED, 
// DEMO_ANALYSIS_COMPLETED, DEMO_TO_REGISTER 